interface ParsedPrettifiableJson {
  data: Record<string, unknown> | unknown[];
  source: string;
}

interface PrettifiableJsonTextOptions {
  enableJsonOutputDetection?: boolean;
  isLiveToolCall: boolean;
  isOutputMessageComplete?: boolean;
  messageRole: string;
  readonly: boolean;
  text: string;
}

const JSON_CODE_FENCE_PATTERN = /^```(?:json)?\s*([\s\S]*?)\s*```$/i;

const unwrapJsonCodeFence = (value: string): string => {
  const trimmed = value.trim();
  const match = trimmed.match(JSON_CODE_FENCE_PATTERN);
  return match?.[1]?.trim() ?? trimmed;
};

export const parsePrettifiableJsonText = (value: string): ParsedPrettifiableJson | null => {
  const unwrapped = unwrapJsonCodeFence(value);

  if (!unwrapped) {
    return null;
  }

  try {
    const parsed = JSON.parse(unwrapped);

    if (Array.isArray(parsed)) {
      return {
        data: parsed,
        source: unwrapped,
      };
    }

    if (parsed && typeof parsed === "object") {
      return {
        data: parsed as Record<string, unknown>,
        source: unwrapped,
      };
    }

    return null;
  } catch {
    return null;
  }
};

export const getPrettifiableJsonText = ({
  enableJsonOutputDetection = false,
  isLiveToolCall,
  isOutputMessageComplete = false,
  messageRole,
  readonly,
  text,
}: PrettifiableJsonTextOptions): ParsedPrettifiableJson | null => {
  if (
    !readonly ||
    !enableJsonOutputDetection ||
    messageRole !== "assistant" ||
    isLiveToolCall ||
    !isOutputMessageComplete
  ) {
    return null;
  }

  return parsePrettifiableJsonText(text);
};
